import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ContentType,
  SearchParams,
  SearchResponse,
  SortOption,
} from "../api/types";
import { useDebounce } from "./useDebounce";

type Fetcher = (params: SearchParams) => Promise<SearchResponse>;

interface UseSearchState {
  /** Current free-text query (updates immediately for a responsive input). */
  query: string;
  /** Selected content-type filters. */
  types: ContentType[];
  /** Selected tag filters. */
  tags: string[];
  /** Selected collection filters. */
  collections: string[];
  /** Selected year, or null for every year. */
  year: string | null;
  /** Current sort. */
  sort: SortOption;
  /** Zero-based page index. */
  page: number;
  /** Latest response (null until the first load completes). */
  response: SearchResponse | null;
  loading: boolean;
  error: string | null;
}

interface UseSearchApi extends UseSearchState {
  setQuery: (q: string) => void;
  toggleType: (t: ContentType) => void;
  toggleTag: (t: string) => void;
  setTags: (tags: string[]) => void;
  toggleCollection: (c: string) => void;
  setYear: (year: string | null) => void;
  setSort: (s: SortOption) => void;
  setPage: (p: number) => void;
  clearFilters: () => void;
}

const PAGE_SIZE = 12;

/** Turn a selected year into the inclusive instant window the API expects. */
function yearWindow(year: string | null): { from?: string; to?: string } {
  if (!year) return {};
  return {
    from: `${year}-01-01T00:00:00Z`,
    to: `${year}-12-31T23:59:59Z`,
  };
}

interface UseSearchOptions {
  initialQuery?: string;
  /**
   * Sort to start on, and the one "Reset" returns to. The galleries open on
   * MIXED; the blog feed and site-wide search stay chronological.
   */
  initialSort?: SortOption;
}

/**
 * Drives a faceted search: owns the query/filter/sort/page state, debounces the
 * text input, refetches whenever inputs change, and exposes the latest response.
 *
 * The galleries, the blog feed and the site-wide search page all use this hook —
 * they differ only in the `fetcher` they pass and where they start the sort, so
 * search behaviour stays identical across the app.
 */
export function useSearch(fetcher: Fetcher, options: UseSearchOptions = {}): UseSearchApi {
  const { initialQuery = "", initialSort = "NEWEST" } = options;
  const [query, setQueryState] = useState(initialQuery);
  const [types, setTypes] = useState<ContentType[]>([]);
  const [tags, setTagsState] = useState<string[]>([]);
  const [collections, setCollections] = useState<string[]>([]);
  const [year, setYearState] = useState<string | null>(null);
  const [sort, setSortState] = useState<SortOption>(initialSort);
  const [page, setPageState] = useState(0);

  const [response, setResponse] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedQuery = useDebounce(query, 300);

  // Guards against out-of-order responses overwriting newer ones.
  const requestId = useRef(0);

  // Reset to the first page whenever the filters (not the page) change.
  useEffect(() => {
    setPageState(0);
  }, [debouncedQuery, types, tags, collections, year, sort]);

  const run = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const data = await fetcher({
        q: debouncedQuery || undefined,
        types,
        tags,
        collections,
        // A year selection is just a date window over the item's own date.
        ...yearWindow(year),
        sort,
        page,
        size: PAGE_SIZE,
      });
      if (id === requestId.current) {
        setResponse(data);
      }
    } catch (err) {
      if (id === requestId.current) {
        setError(err instanceof Error ? err.message : "Search failed");
      }
    } finally {
      if (id === requestId.current) {
        setLoading(false);
      }
    }
  }, [fetcher, debouncedQuery, types, tags, collections, year, sort, page]);

  useEffect(() => {
    void run();
  }, [run]);

  const setQuery = useCallback((q: string) => setQueryState(q), []);
  const setSort = useCallback((s: SortOption) => setSortState(s), []);
  const setPage = useCallback((p: number) => setPageState(Math.max(0, p)), []);
  const setTags = useCallback((next: string[]) => setTagsState(next), []);
  const setYear = useCallback((next: string | null) => setYearState(next), []);

  const toggleType = useCallback((t: ContentType) => {
    setTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );
  }, []);

  const toggleTag = useCallback((t: string) => {
    setTagsState((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );
  }, []);

  const toggleCollection = useCallback((c: string) => {
    setCollections((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
    );
  }, []);

  const clearFilters = useCallback(() => {
    setTypes([]);
    setTagsState([]);
    setCollections([]);
    setYearState(null);
    setSortState(initialSort);
  }, [initialSort]);

  return {
    query,
    types,
    tags,
    collections,
    year,
    sort,
    page,
    response,
    loading,
    error,
    setQuery,
    toggleType,
    toggleTag,
    setTags,
    toggleCollection,
    setYear,
    setSort,
    setPage,
    clearFilters,
  };
}
